interface ConsentData {
  consent_uid?: string;
  [key: string]: boolean | string | undefined;
}

declare global {
  interface Window {
    ccc: ConsentData;
  }
}

export class CookieConsent {
  private readonly banner: HTMLElement;
  private readonly form: HTMLFormElement;

  constructor() {
    const banner = document.querySelector<HTMLElement>('.cookieconsent');
    const form = document.querySelector<HTMLFormElement>('#elc-cookie-consent-form');
    if (!banner || !form) return;

    this.banner = banner;
    this.form = form;

    // Server-side Twig adds cookieconsent__hidden if consent already given.
    // If no consent yet, make the banner visible.
    if (!this.banner.classList.contains('cookieconsent__hidden')) {
      this.toggleLayer(true);
    }

    document.getElementById('accept_all')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.submitAllConsent();
    });

    document.getElementById('accept_selection')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.submitConsent();
    });

    document.querySelector<HTMLElement>('.component-data-protection-layer-open')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleLayer(true);
    });

    this.initPaneToggles();
    this.loadConsentData();
  }

  private toggleLayer(visible: boolean): void {
    this.banner.classList.toggle('cookieconsent__hidden', !visible);
    this.banner.classList.toggle('cookieconsent__visible', visible);

    document.querySelector<HTMLElement>('.component-data-protection-layer-open')
      ?.setAttribute('aria-pressed', visible ? 'true' : 'false');
  }

  private submitConsent(): void {
    this.toggleLayer(false);
    this.post(this.serializeForm());
  }

  private submitAllConsent(): void {
    this.form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
      cb.checked = true;
    });
    this.toggleLayer(false);
    this.post(this.serializeForm() + '&acceptAll=true');
  }

  private post(data: string): void {
    const url = this.form.dataset['url'] ?? '';
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.send(data);

    xhr.onreadystatechange = (): void => {
      if (xhr.readyState !== 4) return;
      if (xhr.status !== 200) {
        console.error('Cookie consent submission failed:', xhr.status);
        return;
      }
      this.loadConsentData();
      if (this.form.dataset['refresh'] === 'true' && this.form.dataset['refreshtime']) {
        setTimeout((): void => { location.reload(); }, Number(this.form.dataset['refreshtime']));
      }
    };
  }

  private loadConsentData(): void {
    const showUrl = this.form.dataset['show'];
    if (!showUrl) return;

    const xhr = new XMLHttpRequest();
    xhr.open('GET', showUrl);
    xhr.send();

    xhr.onreadystatechange = (): void => {
      if (xhr.readyState !== 4) return;
      window.ccc = xhr.status === 200 && xhr.response !== 'null'
        ? (JSON.parse(xhr.response) as ConsentData)
        : {};
    };
  }

  private serializeForm(): string {
    const serialized: string[] = [];
    for (const el of Array.from(this.form.elements)) {
      const field = el as HTMLInputElement;
      if (!field.name || field.disabled) continue;
      if (['file', 'reset', 'button', 'submit'].includes(field.type)) continue;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) continue;
      serialized.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`);
    }
    return serialized.join('&');
  }

  private initPaneToggles(): void {
    this.banner.querySelectorAll<HTMLElement>('.js_pane[data-toggle]').forEach((btn) => {
      const targetKey = btn.getAttribute('data-toggle');
      const target = this.banner.querySelector<HTMLElement>(`[data-toggle-target="${targetKey}"]`);
      if (!target) return;

      target.style.display = 'none';
      btn.addEventListener('click', () => {
        const isOpen = target.style.display !== 'none';
        target.style.display = isOpen ? 'none' : 'block';
        btn.classList.toggle('gui--toggle__active', !isOpen);
      });
    });
  }
}
